import React, { useState, useEffect, useRef } from 'react';
import { AthleteProfile, HighlightClip, SportType } from '../types';
import { HighlightEmptyIllustration } from './illustrations/HighlightEmptyIllustration';
import { ShareConfirmationNotification, ShareConfirmationData } from './notifications/ShareConfirmationNotification';
import { 
  Film, 
  Video, 
  Sparkles, 
  Trophy, 
  PlusCircle, 
  Check, 
  Play, 
  Pause,
  Heart, 
  Eye, 
  Share2, 
  Upload, 
  Flame, 
  Award, 
  ChevronRight, 
  Zap, 
  CheckCircle2, 
  Filter, 
  Lock,
  ArrowUpRight,
  ArrowUpDown,
  Clock,
  AlertTriangle,
  Flag,
  HardDrive,
  CloudDownload,
  Wifi,
  X,
  VolumeX,
  RefreshCw,
  ShieldAlert,
  Repeat,
  Users,
  MessageSquare,
  Send,
  Smile,
  Copy,
  Radio,
  Share,
  Download,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import { getSampleFallbackVideo } from '../utils/videoStorage';
import { getHostedVideoEmbedUrl } from '../utils/videoProvider';
import { subscribeHighlights, saveHighlightInteractionToFirestore } from '../services/firestoreService';
import { triggerHaptic } from '../utils/haptics';

interface Challenge {
  id: string;
  title: string;
  sport: SportType;
  level: number; // 1, 2, 3
  description: string;
  xpReward: number;
  sampleVideoUrl?: string;
  thumbnailUrl?: string;
}

interface HighlightReelViewProps {
  user: AthleteProfile;
  allAthletes: AthleteProfile[];
  onOpenUploadModal: (presetTitle?: string, presetSport?: SportType) => void;
  onEarnXp: (amount: number, source: string) => void;
  onUpdateProfile?: (updated: Partial<AthleteProfile>) => void;
}

export type HighlightSortOption = 'recent' | 'viewed' | 'liked';

export const HighlightReelView: React.FC<HighlightReelViewProps> = ({
  user,
  allAthletes,
  onOpenUploadModal,
  onEarnXp,
  onUpdateProfile,
}) => {
  const [activeSportFilter, setActiveSportFilter] = useState<SportType | 'all'>(user.primarySport || 'basketball');
  const [selectedChallengeLevel, setSelectedChallengeLevel] = useState<number>(1);
  const [completedChallengeIds, setCompletedChallengeIds] = useState<string[]>([
    'bball_layup_1', // default completed for demo
  ]);

  // Dropdown Sort & Filter States for Highlight Reels
  const [sortOption, setSortOption] = useState<HighlightSortOption>('recent');
  const [streamSportFilter, setStreamSportFilter] = useState<SportType | 'all'>('all');
  
  const allCommunityHighlights: HighlightClip[] = user.highlights || [];

  const [activeClip, setActiveClip] = useState<HighlightClip | null>(allCommunityHighlights[0] || null);
  const [clipLikes, setClipLikes] = useState<Record<string, number>>({});
  const [clipViews, setClipViews] = useState<Record<string, number>>({});
  const [likedClipIds, setLikedClipIds] = useState<string[]>([]);
  const [cachedUrls, setCachedUrls] = useState<Record<string, string>>({});
  const [videoLoadStatuses, setVideoLoadStatuses] = useState<Record<string, string>>({});

  // Auto-Play & Continuous Loop Settings
  const [isAutoLoopEnabled, setIsAutoLoopEnabled] = useState<boolean>(true);
  const [hoveredClipId, setHoveredClipId] = useState<string | null>(null);
  const [isAutoPlayOnHoverEnabled, setIsAutoPlayOnHoverEnabled] = useState<boolean>(true);

  // Report Issue Modal & Technical Review state
  const [reportingClip, setReportingClip] = useState<HighlightClip | null>(null);
  const [reportReason, setReportReason] = useState<string>('Video playback failed / black screen');
  const [reportNotes, setReportNotes] = useState<string>('');
  const [reportedClipIds, setReportedClipIds] = useState<string[]>([]);
  const [reportToastMessage, setReportToastMessage] = useState<string | null>(null);

  // Re-post to Feed State
  const [repostingClip, setRepostingClip] = useState<HighlightClip | null>(null);
  const [repostShoutoutMessage, setRepostShoutoutMessage] = useState<string>('');

  // Ephemeral Watch Party Room State
  const [isWatchPartyOpen, setIsWatchPartyOpen] = useState<boolean>(false);
  const [watchPartyClip, setWatchPartyClip] = useState<HighlightClip | null>(null);
  const [watchPartyRoomId, setWatchPartyRoomId] = useState<string>('WP-7729');
  const [watchPartyIsPlaying, setWatchPartyIsPlaying] = useState<boolean>(true);
  const [watchPartyCurrentTime, setWatchPartyCurrentTime] = useState<number>(0);
  const [watchPartyChatInput, setWatchPartyChatInput] = useState<string>('');
  const [watchPartyCopied, setWatchPartyCopied] = useState<boolean>(false);
  const [watchPartyActiveTab, setWatchPartyActiveTab] = useState<'chat' | 'members'>('chat');

  const [watchPartyFloatingReactions, setWatchPartyFloatingReactions] = useState<
    Array<{ id: string; emoji: string; left: number }>
  >([]);

  const [watchPartyMessages, setWatchPartyMessages] = useState<
    Array<{ id: string; senderName: string; senderAvatar: string; text: string; timestamp: string; isSystem?: boolean }>
  >([
    {
      id: 'wp_m1',
      senderName: 'Playground League',
      senderAvatar: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=200',
      text: '⚡ Watch Party Room created! Synced real-time playback enabled.',
      timestamp: 'Just now',
      isSystem: true,
    },
    {
      id: 'wp_m2',
      senderName: 'Marcus Vance',
      senderAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200',
      text: 'Yo look at that drive at 0:08!! Pure speed 🔥',
      timestamp: '1 min ago',
    },
    {
      id: 'wp_m3',
      senderName: 'Elena Rostova',
      senderAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200',
      text: 'The defense stood no chance on that stepback 😱',
      timestamp: '30s ago',
    },
  ]);

  const watchPartyVideoRef = useRef<HTMLVideoElement | null>(null);

  // Floating Quick Share & Social Media Preview Image Generator State
  const [shareConfirmation, setShareConfirmation] = useState<ShareConfirmationData | null>(null);
  const [isQuickShareModalOpen, setIsQuickShareModalOpen] = useState<boolean>(false);
  const [quickShareClip, setQuickShareClip] = useState<HighlightClip | null>(null);
  const [quickShareImageUrl, setQuickShareImageUrl] = useState<string | null>(null);
  const [quickShareCopied, setQuickShareCopied] = useState<boolean>(false);
  const quickShareCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const handleOpenQuickShare = (clip: HighlightClip) => {
    triggerHaptic('medium');
    setQuickShareClip(clip);
    setIsQuickShareModalOpen(true);
    setQuickShareCopied(false);
  };

  // Generate formatted social media preview image on canvas
  useEffect(() => {
    if (!isQuickShareModalOpen || !quickShareClip || !quickShareCanvasRef.current) return;

    const canvas = quickShareCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 1200;
    canvas.height = 630;

    // Background Gradient (Dark Indigo to Deep Purple)
    const bgGradient = ctx.createLinearGradient(0, 0, 1200, 630);
    bgGradient.addColorStop(0, '#090d16');
    bgGradient.addColorStop(0.5, '#1e1b4b');
    bgGradient.addColorStop(1, '#090d16');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, 1200, 630);

    // Decorative Accent Grid Lines & Glowing Circles
    ctx.strokeStyle = 'rgba(163, 230, 53, 0.15)';
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.arc(1000, 315, 200, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(251, 191, 36, 0.2)';
    ctx.beginPath();
    ctx.arc(200, 500, 150, 0, Math.PI * 2);
    ctx.stroke();

    // Top Header Banner
    ctx.fillStyle = '#a3e635';
    ctx.fillRect(60, 50, 120, 8);

    ctx.fillStyle = '#ffffff';
    ctx.font = '900 24px sans-serif';
    ctx.fillText('PLAYGROUND LEAGUE • HIGHLIGHT REEL SPOTLIGHT', 200, 60);

    // Athlete Info Card Container
    ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
    if (typeof (ctx as any).roundRect === 'function') {
      (ctx as any).roundRect(60, 90, 1080, 480, 24);
      ctx.fill();
    } else {
      ctx.fillRect(60, 90, 1080, 480);
    }

    ctx.strokeStyle = 'rgba(163, 230, 53, 0.4)';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Sport Tag Badge
    ctx.fillStyle = '#a3e635';
    ctx.fillRect(100, 130, 180, 36);

    ctx.fillStyle = '#000000';
    ctx.font = '900 18px sans-serif';
    ctx.fillText(quickShareClip.sport.toUpperCase() + ' REEL ⚡', 120, 154);

    // Highlight Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'italic 900 32px sans-serif';
    const titleText = quickShareClip.title.length > 42 ? quickShareClip.title.substring(0, 42) + '...' : quickShareClip.title;
    ctx.fillText(titleText, 100, 220);

    // Description text
    ctx.fillStyle = '#cbd5e1';
    ctx.font = '500 20px sans-serif';
    const description = quickShareClip.description || '';
    const descText = description.length > 68 ? description.substring(0, 68) + '...' : description;
    ctx.fillText(descText, 100, 260);

    // Stats Row (Views, Likes, Duration)
    ctx.fillStyle = '#1e1b4b';
    ctx.fillRect(100, 300, 580, 70);

    ctx.fillStyle = '#fbbf24';
    ctx.font = 'bold 22px sans-serif';
    const currentViews = clipViews[quickShareClip.id] ?? quickShareClip.views;
    const currentLikes = clipLikes[quickShareClip.id] ?? quickShareClip.likes;
    ctx.fillText(`👁️ ${currentViews} Views   ❤️ ${currentLikes} Likes   ⏱️ 00:${quickShareClip.durationSeconds}s`, 120, 342);

    // Athlete Profile Badge
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText(`ATHLETE: ${user?.name || 'Playground League Athlete'}`, 100, 420);

    ctx.fillStyle = '#a3e635';
    ctx.font = '600 18px sans-serif';
    ctx.fillText(`LEVEL ${user?.level || 1} • ${user?.levelTitle || 'Rookie'} • ${user?.schoolOrLeague || 'Playground League'}`, 100, 450);

    // Footer Branding & QR Placeholder Box
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(720, 300, 380, 230);
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(720, 300, 380, 230);

    ctx.fillStyle = '#a3e635';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('PLAYGROUND LEAGUE', 750, 350);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '14px monospace';
    ctx.fillText('Scan or visit to watch full clip:', 750, 380);
    ctx.fillText('ai.studio/playgroundleague', 750, 410);

    ctx.fillStyle = '#fbbf24';
    ctx.font = 'bold 16px sans-serif';
    ctx.fillText('🔥 Rank #1 Local Playground Feed', 750, 460);

    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '12px sans-serif';
    ctx.fillText(`Generated ${new Date().toLocaleDateString()}`, 750, 500);

    try {
      const url = canvas.toDataURL('image/png');
      setQuickShareImageUrl(url);
    } catch (e) {
      console.warn('Canvas toDataURL error:', e);
    }
  }, [isQuickShareModalOpen, quickShareClip, clipViews, clipLikes, user]);

  const handleStartWatchParty = (clip: HighlightClip) => {
    const roomId = `WP-${Math.floor(1000 + Math.random() * 9000)}`;
    setWatchPartyRoomId(roomId);
    setWatchPartyClip(clip);
    setWatchPartyIsPlaying(true);
    setWatchPartyCurrentTime(0);
    setIsWatchPartyOpen(true);
    onEarnXp(15, 'Created Highlight Watch Party Room');
  };

  const handleToggleWatchPartyPlayback = () => {
    setWatchPartyIsPlaying((prev) => {
      const next = !prev;
      if (watchPartyVideoRef.current) {
        if (next) watchPartyVideoRef.current.play();
        else watchPartyVideoRef.current.pause();
      }
      return next;
    });

    setWatchPartyMessages((prev) => [
      ...prev,
      {
        id: `wp_sync_${Date.now()}`,
        senderName: user?.name || 'Athlete',
        senderAvatar: user?.avatar || '',
        text: !watchPartyIsPlaying ? 'resumed playback ▶️' : 'paused video for discussion ⏸️',
        timestamp: 'Just now',
        isSystem: true,
      },
    ]);
  };

  const handleTriggerWatchPartyReaction = (emoji: string) => {
    const newReaction = {
      id: `rx_${Date.now()}_${Math.random()}`,
      emoji,
      left: Math.floor(15 + Math.random() * 70),
    };
    setWatchPartyFloatingReactions((prev) => [...prev, newReaction]);

    setTimeout(() => {
      setWatchPartyFloatingReactions((prev) => prev.filter((r) => r.id !== newReaction.id));
    }, 2200);

    setWatchPartyMessages((prev) => [
      ...prev,
      {
        id: `wp_rx_msg_${Date.now()}`,
        senderName: user?.name || 'Athlete',
        senderAvatar: user?.avatar || '',
        text: `reacted ${emoji}`,
        timestamp: 'Just now',
        isSystem: true,
      },
    ]);
  };

  const handleSendWatchPartyMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!watchPartyChatInput.trim()) return;

    const newMsg = {
      id: `wp_msg_${Date.now()}`,
      senderName: user?.name || 'Athlete',
      senderAvatar: user?.avatar || '',
      text: watchPartyChatInput.trim(),
      timestamp: 'Just now',
    };

    setWatchPartyMessages((prev) => [...prev, newMsg]);
    setWatchPartyChatInput('');
  };

  const handleSubmitRepost = () => {
    if (!repostingClip) return;

    const repostedClip: HighlightClip = {
      id: `repost_${Date.now()}`,
      title: `Re-posted: ${repostingClip.title}`,
      sport: repostingClip.sport,
      videoUrl: repostingClip.videoUrl,
      thumbnailUrl: repostingClip.thumbnailUrl,
      durationSeconds: repostingClip.durationSeconds,
      createdAt: 'Just now',
      views: 1,
      likes: 0,
      description: repostShoutoutMessage.trim() || `Shoutout to ${repostingClip.title}! 🔥`,
      shoutout: repostShoutoutMessage.trim(),
      repostedFrom: repostingClip.title,
      repostedBy: user?.name || 'Athlete',
    };

    const currentHighlights = user.highlights || [];
    const updatedHighlights = [repostedClip, ...currentHighlights];

    if (onUpdateProfile) {
      onUpdateProfile({ highlights: updatedHighlights });
    }

    onEarnXp(25, 'Re-posted Highlight Reel to Profile Feed');
    setReportToastMessage(`🚀 Re-posted "${repostingClip.title}" to your profile feed! (+25 XP)`);
    setRepostingClip(null);
    setRepostShoutoutMessage('');
  };

  // Real-time Firestore sync for highlight likes & view counts
  useEffect(() => {
    const unsubscribe = subscribeHighlights((highlightsMap) => {
      setClipLikes((prev) => {
        const updated = { ...prev };
        Object.keys(highlightsMap).forEach((id) => {
          if (typeof highlightsMap[id].likes === 'number') {
            updated[id] = highlightsMap[id].likes;
          }
        });
        return updated;
      });

      setClipViews((prev) => {
        const updated = { ...prev };
        Object.keys(highlightsMap).forEach((id) => {
          if (typeof highlightsMap[id].views === 'number') {
            updated[id] = highlightsMap[id].views;
          }
        });
        return updated;
      });
    });

    return () => unsubscribe();
  }, []);

  // Increment view count for the active theater clip and sync to Firestore
  useEffect(() => {
    if (!activeClip?.id) return;
    const targetClip = allCommunityHighlights.find((c) => c.id === activeClip.id);
    const currentViews = clipViews[activeClip.id] ?? targetClip?.views ?? 0;
    const currentLikes = clipLikes[activeClip.id] ?? targetClip?.likes ?? 0;
    const newViews = currentViews + 1;

    setClipViews((prev) => ({
      ...prev,
      [activeClip.id]: newViews,
    }));

    // Automated Reward System: Grant extra XP on view milestone threshold
    if (newViews > 0 && newViews % 5 === 0) {
      const xpBonus = 20;
      onEarnXp(xpBonus, `Highlight Reel Community Engagement (${newViews} Views Milestone)`);
      setReportToastMessage(`👁️ Automated Community Reward: +${xpBonus} XP granted for reaching ${newViews} views! Total XP & Level updated.`);
      setTimeout(() => setReportToastMessage(null), 4000);
    }

    // Sync updated views to Firestore
    saveHighlightInteractionToFirestore(activeClip.id, currentLikes, newViews);
  }, [activeClip?.id]);

  // Resolve hosted links directly; video bytes are never copied into browser storage.
  useEffect(() => {
    let isMounted = true;
    allCommunityHighlights.forEach(async (clip) => {
      if (isMounted) {
        setVideoLoadStatuses((prev) => ({ ...prev, [clip.id]: getHostedVideoEmbedUrl(clip.videoUrl) ? 'Hosted by provider' : 'Streaming online' }));
      }
      if (isMounted) {
        setCachedUrls((prev) => ({ ...prev, [clip.id]: clip.videoUrl || getSampleFallbackVideo(clip.sport) }));
      }
    });
    return () => {
      isMounted = false;
    };
  }, [user.highlights]);

  useEffect(() => {
    if (!activeClip && allCommunityHighlights.length > 0) {
      setActiveClip(allCommunityHighlights[0]);
    }
  }, [activeClip, user.highlights]);

  const handleLikeClip = (clipId: string) => {
    const isAlreadyLiked = likedClipIds.includes(clipId);
    const targetClip = allCommunityHighlights.find((c) => c.id === clipId);
    const baseLikes = targetClip?.likes ?? 0;
    const currentLikes = clipLikes[clipId] ?? baseLikes;
    const newLikes = isAlreadyLiked ? Math.max(0, currentLikes - 1) : currentLikes + 1;
    const currentViews = clipViews[clipId] ?? targetClip?.views ?? 0;

    // Toggle liked state locally
    setLikedClipIds((prev) =>
      isAlreadyLiked ? prev.filter((id) => id !== clipId) : [...prev, clipId]
    );

    // Update likes state
    setClipLikes((prev) => ({
      ...prev,
      [clipId]: newLikes,
    }));

    if (!isAlreadyLiked) {
      const isUserClip = user.highlights?.some((h) => h.id === clipId);
      const earnedAmount = isUserClip ? 75 : 25;
      onEarnXp(earnedAmount, isUserClip ? 'Creator Like Reward' : 'Community Highlight Like Bonus');
      setReportToastMessage(`❤️ Automated Community Reward: +${earnedAmount} XP granted for highlight reel interaction! Total XP & Level updated.`);
      setTimeout(() => setReportToastMessage(null), 4000);
    }

    // Sync updated likes & views to Firestore
    saveHighlightInteractionToFirestore(clipId, newLikes, currentViews);
  };

  const handleSubmitIssueReport = () => {
    if (!reportingClip) return;
    setReportedClipIds((prev) => [...prev, reportingClip.id]);
    setReportToastMessage(`⚠️ Issue reported for "${reportingClip.title}". Admin Dashboard notified for technical review.`);
    setTimeout(() => setReportToastMessage(null), 5000);
    setReportingClip(null);
    setReportNotes('');
  };

  // Sorted and Filtered Highlights computation
  const sortedAndFilteredHighlights = [...allCommunityHighlights]
    .filter((clip) => streamSportFilter === 'all' || clip.sport === streamSportFilter)
    .sort((a, b) => {
      if (sortOption === 'viewed') {
        return b.views - a.views;
      }
      if (sortOption === 'liked') {
        const likesA = clipLikes[a.id] ?? a.likes;
        const likesB = clipLikes[b.id] ?? b.likes;
        return likesB - likesA;
      }
      // 'recent' - default chronological / order
      return 0;
    });

  // Ensure active clip stays valid when filters change
  useEffect(() => {
    if (sortedAndFilteredHighlights.length > 0) {
      const isStillAvailable = activeClip && sortedAndFilteredHighlights.some((c) => c.id === activeClip.id);
      if (!isStillAvailable) {
        setActiveClip(sortedAndFilteredHighlights[0]);
      }
    }
  }, [sortOption, streamSportFilter]);

  // Sport Specific Level Challenges
  const challenges: Challenge[] = [
    // BASKETBALL
    {
      id: 'bball_layup_1',
      title: 'Upload a Video of a Layup 🏀',
      sport: 'basketball',
      level: 1,
      description: 'Record or upload a clean right or left-hand layup finish at the rim.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_freethrow_1',
      title: 'Upload a Video of a Free Throw 🏀',
      sport: 'basketball',
      level: 1,
      description: 'Record or upload a video sinking a swish free throw from the foul line.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_knockout_1',
      title: 'Knockout / Lightning Elimination ⚡',
      sport: 'basketball',
      level: 1,
      description: 'Record a fast-paced Knockout free-throw game elimination or winning shot.',
      xpReward: 120,
      thumbnailUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_3pointer_1',
      title: 'Upload a Video of a 3-Pointer 🏀',
      sport: 'basketball',
      level: 1,
      description: 'Record or upload a video hitting a 3-point jumper beyond the arc.',
      xpReward: 150,
      thumbnailUrl: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_horse_2',
      title: 'HORSE / PIG Trick Shot Challenge 🐴',
      sport: 'basketball',
      level: 2,
      description: 'Record hitting a creative trick shot (off the backboard, off-balance, or off-hand) in a game of HORSE.',
      xpReward: 200,
      thumbnailUrl: 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_aroundworld_2',
      title: 'Around the World (5 Spots Swish) 🌍',
      sport: 'basketball',
      level: 2,
      description: 'Record sinking shots from 5 key spots around the paint (corner, wing, top, wing, corner).',
      xpReward: 180,
      thumbnailUrl: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_21game_2',
      title: '21 / Cutthroat Playground Score 🏀',
      sport: 'basketball',
      level: 2,
      description: 'Upload a clip from a 1v1v1 playground game of 21 scoring off a rebound or iso jumper.',
      xpReward: 160,
      thumbnailUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_kingcourt_3',
      title: 'King of the Court (1v1 Iso Bucket) 👑',
      sport: 'basketball',
      level: 3,
      description: 'Record an isolation 1v1 bucket or defensive stop to retain the throne in King of the Court.',
      xpReward: 220,
      thumbnailUrl: 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'bball_halfcourt_3',
      title: 'Half-Court Swish Buzzer Beater 🎯',
      sport: 'basketball',
      level: 3,
      description: 'Record a half-court shot attempt draining straight into the net.',
      xpReward: 300,
      thumbnailUrl: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80&w=400',
    },

    // SOCCER
    {
      id: 'soccer_pk_1',
      title: 'Upload a Penalty Kick Video ⚽',
      sport: 'soccer',
      level: 1,
      description: 'Upload a clean penalty kick placement into the upper or lower corner.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'soccer_juggle_1',
      title: 'Upload a 10-Juggle Skill Video ⚽',
      sport: 'soccer',
      level: 1,
      description: 'Record a video juggling the ball 10 consecutive times without letting it hit the ground.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1517466787929-bc90951d0974?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'soccer_crossbar_2',
      title: 'Crossbar Challenge (20 Yards) ⚽',
      sport: 'soccer',
      level: 2,
      description: 'Record striking the goal crossbar directly from 20 yards out.',
      xpReward: 200,
      thumbnailUrl: 'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'soccer_worldcup_2',
      title: 'World Cup Knockout Volley 🏆',
      sport: 'soccer',
      level: 2,
      description: 'Record an airborne elimination volley or header finish in a game of World Cup.',
      xpReward: 175,
      thumbnailUrl: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&q=80&w=400',
    },

    // BASEBALL & SOFTBALL
    {
      id: 'base_swing_1',
      title: 'Upload a Batting Cage Swing ⚾',
      sport: 'baseball',
      level: 1,
      description: 'Record a video of a clean line-drive swing off a tee or pitch in the cage.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'base_500game_1',
      title: '500 / Fly-Ball Catching Challenge 🥎',
      sport: 'baseball',
      level: 1,
      description: 'Record catching pop-flys or making diving catches in a neighborhood game of 500.',
      xpReward: 130,
      thumbnailUrl: 'https://images.unsplash.com/photo-1562077772-3bd90403f7f0?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'base_hrderby_2',
      title: 'Home Run Derby Blast ⚾',
      sport: 'baseball',
      level: 2,
      description: 'Record a deep distance swing clearing the out-field fence in HR Derby.',
      xpReward: 200,
      thumbnailUrl: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&q=80&w=400',
    },

    // FOOTBALL
    {
      id: 'fb_catch_1',
      title: 'Upload a 15-Yard Catch Video 🏈',
      sport: 'football',
      level: 1,
      description: 'Upload a video running a route and securing a 15-yard pass catch.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'fb_target_2',
      title: 'Target Passing Precision 🎯',
      sport: 'football',
      level: 2,
      description: 'Throw a spiral pass directly through a pop-up net or tire target.',
      xpReward: 180,
      thumbnailUrl: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?auto=format&fit=crop&q=80&w=400',
    },

    // PICKLEBALL
    {
      id: 'pb_dink_1',
      title: 'Upload a Kitchen Dink Rally 🏓',
      sport: 'pickleball',
      level: 1,
      description: 'Record a video maintaining a 5-dink patient exchange at the kitchen line.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'pb_kingkitchen_2',
      title: 'King of the Kitchen Drop 👑',
      sport: 'pickleball',
      level: 2,
      description: 'Upload a third-shot drop winner into the kitchen to claim the point.',
      xpReward: 175,
      thumbnailUrl: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&q=80&w=400',
    },

    // VOLLEYBALL
    {
      id: 'vb_serve_1',
      title: 'Upload an Overhand Serve Video 🏐',
      sport: 'volleyball',
      level: 1,
      description: 'Record an overhand serve clearing the net into the deep court zone.',
      xpReward: 100,
      thumbnailUrl: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'vb_spike_1',
      title: 'Upload a Volleyball Spike / Kill 💥',
      sport: 'volleyball',
      level: 1,
      description: 'Record or upload a high-flying spike attack off a set into the opponent floor.',
      xpReward: 120,
      thumbnailUrl: 'https://images.unsplash.com/photo-1592656094267-764a45160876?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'vb_block_2',
      title: 'Stuff Block at the Net 🛡️',
      sport: 'volleyball',
      level: 2,
      description: 'Record a timed jump and two-handed rejection block at the top of the tape.',
      xpReward: 180,
      thumbnailUrl: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&q=80&w=400',
    },
    {
      id: 'vb_pancake_3',
      title: 'Diving Pancake Save / Dig 🥞',
      sport: 'volleyball',
      level: 3,
      description: 'Record a diving pancake floor save keeping a tipped ball in play for a rally continuation.',
      xpReward: 250,
      thumbnailUrl: 'https://images.unsplash.com/photo-1592656094267-764a45160876?auto=format&fit=crop&q=80&w=400',
    },
  ];

  if (allCommunityHighlights.length === 0 || !activeClip) {
    return (
      <HighlightEmptyIllustration
        title="No Highlight Reels Yet"
        description="Verified athlete reels will appear here after someone publishes a hosted video."
        selectedSport={streamSportFilter}
        onUploadHighlight={() => onOpenUploadModal()}
      />
    );
  }

  // Filter challenges based on sport and level
  const filteredChallenges = challenges.filter((c) => {
    const matchesSport = activeSportFilter === 'all' || c.sport === activeSportFilter;
    const matchesLevel = c.level === selectedChallengeLevel;
    return matchesSport && matchesLevel;
  });

  // Calculate XP Percentage
  const currentXp = user.xp || 0;
  const xpToNext = user.xpToNextLevel || 4500;
  const progressPercent = Math.min(100, Math.round((currentXp / xpToNext) * 100));

  const handleCompleteChallengeManual = (c: Challenge) => {
    if (completedChallengeIds.includes(c.id)) return;
    setCompletedChallengeIds((prev) => [...prev, c.id]);
    onEarnXp(c.xpReward, `Level ${c.level} Challenge: ${c.title}`);
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-8 animate-fadeIn text-white">
      {/* Top Banner Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Film className="w-6 h-6 text-lime-400 stroke-[2.5]" />
            <h1 className="text-2xl font-black italic uppercase tracking-tight text-white">
              30s Game Highlight Reel & Level Challenges
            </h1>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold max-w-2xl">
            Upload 30-second clips, complete sport-specific level challenges to earn XP, fill your progress bar, and level up your athlete profile.
          </p>
        </div>

        <button
          onClick={() => onOpenUploadModal()}
          className="px-5 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-lg shadow-lime-400/20 transition flex items-center justify-center space-x-2 self-start lg:self-auto"
        >
          <PlusCircle className="w-5 h-5 stroke-[2.5]" />
          <span>Record / Upload 30s Reel (+50 XP)</span>
        </button>
      </div>

      {/* HIGHLIGHT REEL DROPDOWN FILTER TOOLBAR */}
      <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-indigo-950 p-4 sm:p-5 rounded-3xl border border-lime-400/30 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-lime-400/20 text-lime-400 rounded-2xl border border-lime-400/30">
            <Filter className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-black italic uppercase text-white tracking-wide flex items-center gap-2">
              <span>Filter & Sort Highlights</span>
              <span className="text-[10px] font-mono px-2 py-0.5 bg-lime-400 text-black font-extrabold rounded-full">
                {sortedAndFilteredHighlights.length} {sortedAndFilteredHighlights.length === 1 ? 'Clip' : 'Clips'}
              </span>
            </h2>
            <p className="text-[11px] text-indigo-200/70 font-semibold">
              Discover top athlete plays sorted by popularity or sport
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Sort Dropdown */}
          <div className="flex items-center space-x-2 bg-indigo-950/90 px-3.5 py-2 rounded-2xl border border-white/10 hover:border-lime-400/50 transition">
            <ArrowUpDown className="w-4 h-4 text-lime-400 shrink-0" />
            <label className="text-[10px] font-black uppercase italic text-indigo-300/80 shrink-0">
              Sort By:
            </label>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as HighlightSortOption)}
              className="bg-transparent text-xs font-black italic uppercase text-lime-300 outline-none cursor-pointer pr-1"
            >
              <option value="recent" className="bg-indigo-950 text-white font-bold">
                🕒 Most Recent
              </option>
              <option value="viewed" className="bg-indigo-950 text-white font-bold">
                👁️ Most Viewed
              </option>
              <option value="liked" className="bg-indigo-950 text-white font-bold">
                ❤️ Most Liked
              </option>
            </select>
          </div>

          {/* Sport Filter Dropdown */}
          <div className="flex items-center space-x-2 bg-indigo-950/90 px-3.5 py-2 rounded-2xl border border-white/10 hover:border-lime-400/50 transition">
            <Trophy className="w-4 h-4 text-lime-400 shrink-0" />
            <label className="text-[10px] font-black uppercase italic text-indigo-300/80 shrink-0">
              Sport:
            </label>
            <select
              value={streamSportFilter}
              onChange={(e) => setStreamSportFilter(e.target.value as SportType | 'all')}
              className="bg-transparent text-xs font-black italic uppercase text-lime-300 outline-none cursor-pointer pr-1"
            >
              <option value="all" className="bg-indigo-950 text-white font-bold">
                🏆 All Sports
              </option>
              <option value="basketball" className="bg-indigo-950 text-white font-bold">
                🏀 Basketball
              </option>
              <option value="baseball" className="bg-indigo-950 text-white font-bold">
                ⚾ Baseball
              </option>
              <option value="soccer" className="bg-indigo-950 text-white font-bold">
                ⚽ Soccer
              </option>
              <option value="football" className="bg-indigo-950 text-white font-bold">
                🏈 Football
              </option>
              <option value="pickleball" className="bg-indigo-950 text-white font-bold">
                🏓 Pickleball
              </option>
              <option value="tennis" className="bg-indigo-950 text-white font-bold">
                🎾 Tennis
              </option>
              <option value="volleyball" className="bg-indigo-950 text-white font-bold">
                🏐 Volleyball
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* ATHLETE XP PROGRESS BAR CARD */}
      <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-indigo-950 p-6 rounded-[2.5rem] border border-lime-400/30 shadow-2xl space-y-4 relative overflow-hidden">
        {/* Subtle Background Glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <img
                src={user?.avatar || ''}
                alt={user?.name || 'Athlete'}
                className="w-14 h-14 rounded-2xl object-cover ring-4 ring-lime-400/50 shadow-md"
              />
              <span className="absolute -bottom-1 -right-1 bg-lime-400 text-black text-[10px] font-black px-1.5 py-0.5 rounded-full border border-black uppercase">
                Lvl {user?.level || 1}
              </span>
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-black italic text-white tracking-tight">{user?.name || 'Athlete'}</h2>
                <span className="px-2.5 py-0.5 text-[10px] bg-lime-400/20 text-lime-300 font-extrabold rounded-full border border-lime-400/30 uppercase">
                  {user?.levelTitle || 'Rookie'}
                </span>
              </div>
              <p className="text-xs text-indigo-200/70 font-bold">
                Primary Sport: <span className="text-lime-300 uppercase italic font-black">{user?.primarySport || 'Basketball'}</span> • {user?.schoolOrLeague || 'Playground League'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-indigo-950/80 px-4 py-2.5 rounded-2xl border border-white/10">
            <Zap className="w-5 h-5 text-lime-400 fill-lime-400" />
            <div>
              <span className="text-xs font-black text-white block">
                {currentXp.toLocaleString()} / {xpToNext.toLocaleString()} XP
              </span>
              <span className="text-[10px] text-indigo-300 font-semibold">
                {xpToNext - currentXp > 0 ? `${(xpToNext - currentXp).toLocaleString()} XP to Level ${user.level + 1}` : 'Max Level Reached'}
              </span>
            </div>
          </div>
        </div>

        {/* Animated XP Progress Bar */}
        <div className="space-y-1.5 relative z-10">
          <div className="flex justify-between text-xs font-extrabold">
            <span className="text-indigo-200 flex items-center">
              <Trophy className="w-3.5 h-3.5 text-lime-400 mr-1" />
              Level {user.level} Progress
            </span>
            <span className="text-lime-400 font-mono italic">{progressPercent}%</span>
          </div>

          <div className="w-full h-4 bg-indigo-950 rounded-full border border-white/10 overflow-hidden p-0.5 relative">
            <div
              className="h-full bg-gradient-to-r from-lime-500 via-lime-400 to-emerald-400 rounded-full transition-all duration-700 ease-out shadow-lg shadow-lime-400/30"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* SECTION: SPORT-SPECIFIC LEVEL CHALLENGES */}
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="text-xs text-lime-400 font-black italic uppercase tracking-wider block">
              ⚡ Earn XP & Fill Progress Bar
            </span>
            <h2 className="text-xl font-black italic uppercase tracking-tight text-white flex items-center">
              <Award className="w-5 h-5 text-lime-400 mr-2" />
              Sport-Specific Video Challenges
            </h2>
          </div>

          {/* Level Selector Tabs */}
          <div className="flex bg-indigo-950 p-1 rounded-2xl border border-white/10 text-xs font-black italic uppercase">
            {[1, 2, 3].map((lvl) => (
              <button
                key={lvl}
                onClick={() => setSelectedChallengeLevel(lvl)}
                className={`px-4 py-2 rounded-xl transition flex items-center space-x-1.5 ${
                  selectedChallengeLevel === lvl
                    ? 'bg-lime-400 text-black shadow-md'
                    : 'text-indigo-300 hover:text-white'
                }`}
              >
                <span>Level {lvl}</span>
                <span className="text-[9px] opacity-80">
                  ({lvl === 1 ? 'Rookie' : lvl === 2 ? 'Varsity' : 'All-Star'})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Sport Selector Filter Pills */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2 text-xs font-bold">
          <span className="text-[10px] uppercase font-black text-indigo-300/60 shrink-0">Filter Sport:</span>
          {[
            { id: 'all', label: 'All Sports 🏆' },
            { id: 'volleyball', label: 'Volleyball 🏐' },
            { id: 'basketball', label: 'Basketball 🏀' },
            { id: 'soccer', label: 'Soccer ⚽' },
            { id: 'baseball', label: 'Baseball ⚾' },
            { id: 'football', label: 'Football 🏈' },
            { id: 'pickleball', label: 'Pickleball 🏓' },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSportFilter(s.id as any)}
              className={`px-3 py-1.5 rounded-xl transition shrink-0 ${
                activeSportFilter === s.id
                  ? 'bg-lime-400 text-black font-black italic'
                  : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Challenge Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredChallenges.length === 0 ? (
            <div className="col-span-full p-8 text-center bg-indigo-950/40 rounded-3xl border border-white/5 space-y-2">
              <Sparkles className="w-8 h-8 text-indigo-400 mx-auto" />
              <p className="text-sm font-extrabold text-white">No Level {selectedChallengeLevel} challenges found for this sport filter.</p>
              <p className="text-xs text-indigo-200/60">Switch sport filters or select Level 1 Rookie challenges!</p>
            </div>
          ) : (
            filteredChallenges.map((challenge) => {
              const isCompleted = completedChallengeIds.includes(challenge.id);

              return (
                <div
                  key={challenge.id}
                  className={`p-5 rounded-3xl border transition flex flex-col justify-between space-y-4 relative overflow-hidden ${
                    isCompleted
                      ? 'bg-indigo-950/80 border-lime-400/50 text-white'
                      : 'bg-indigo-950/60 border-white/10 text-white hover:border-lime-400/30'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-indigo-900 text-lime-300 border border-lime-400/30">
                        {challenge.sport} • Level {challenge.level}
                      </span>
                      <span className="text-xs font-black text-lime-400 flex items-center">
                        <Zap className="w-3.5 h-3.5 mr-0.5 fill-lime-400" />
                        +{challenge.xpReward} XP
                      </span>
                    </div>

                    <h3 className="font-extrabold text-sm text-white leading-tight">{challenge.title}</h3>
                    <p className="text-xs text-indigo-200/70 font-medium leading-relaxed">
                      {challenge.description}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="pt-2">
                    {isCompleted ? (
                      <div className="w-full py-2.5 bg-lime-400/20 text-lime-300 font-extrabold text-xs rounded-xl border border-lime-400/40 flex items-center justify-center space-x-1.5">
                        <CheckCircle2 className="w-4 h-4 text-lime-400" />
                        <span>Completed (+{challenge.xpReward} XP Claimed)</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => {
                            onOpenUploadModal(challenge.title, challenge.sport);
                            handleCompleteChallengeManual(challenge);
                          }}
                          className="w-full py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center justify-center space-x-1.5"
                        >
                          <Video className="w-4 h-4" />
                          <span>Upload Video & Earn XP</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleCompleteChallengeManual(challenge)}
                          className="w-full py-1 text-[10px] text-indigo-300 hover:text-lime-300 font-bold transition text-center"
                        >
                          Already have a clip? Claim +{challenge.xpReward} XP
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* SECTION: INTERACTIVE 30s HIGHLIGHT REEL THEATER & COMMUNITY FEED */}
      {/* Toast Notification for Technical Issue Reports */}
      {reportToastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-indigo-950 text-white font-extrabold text-xs px-5 py-3.5 rounded-2xl shadow-2xl border border-amber-400/60 flex items-center space-x-3 animate-fadeIn">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
          <span>{reportToastMessage}</span>
          <button onClick={() => setReportToastMessage(null)} className="p-1 hover:bg-white/10 rounded-lg transition ml-2">
            <X className="w-4 h-4 text-white/70" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ACTIVE VIDEO THEATER PLAYER */}
        <div className="lg:col-span-7 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black italic uppercase text-lime-400 flex items-center">
              <Play className="w-4 h-4 mr-1.5 fill-lime-400" />
              Featured 30s Reel Theater
            </span>

            <div className="flex items-center space-x-2">
              {/* Auto-Looping Toggle Button */}
              <button
                type="button"
                onClick={() => setIsAutoLoopEnabled(!isAutoLoopEnabled)}
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold transition flex items-center space-x-1 border ${
                  isAutoLoopEnabled
                    ? 'bg-lime-400/20 text-lime-300 border-lime-400/40'
                    : 'bg-indigo-950 text-indigo-400 border-white/10'
                }`}
                title="Toggle continuous video auto-looping"
              >
                <Repeat className={`w-3 h-3 ${isAutoLoopEnabled ? 'text-lime-400' : ''}`} />
                <span>Auto-Loop: {isAutoLoopEnabled ? 'ON 🔁' : 'OFF'}</span>
              </button>

              {/* Load Status Indicator Badge */}
              <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold border ${
                videoLoadStatuses[activeClip.id] === 'Loaded from Cache'
                  ? 'bg-emerald-950/90 text-emerald-300 border-emerald-500/50'
                  : videoLoadStatuses[activeClip.id] === 'Downloading from Storage'
                  ? 'bg-sky-950/90 text-sky-300 border-sky-500/50'
                  : 'bg-amber-950/90 text-amber-300 border-amber-500/50'
              }`}>
                {videoLoadStatuses[activeClip.id] === 'Loaded from Cache' ? (
                  <HardDrive className="w-3 h-3 text-emerald-400 shrink-0" />
                ) : videoLoadStatuses[activeClip.id] === 'Downloading from Storage' ? (
                  <CloudDownload className="w-3 h-3 text-sky-400 animate-pulse shrink-0" />
                ) : (
                  <Wifi className="w-3 h-3 text-amber-400 animate-spin shrink-0" />
                )}
                <span>{videoLoadStatuses[activeClip.id] || 'Resolving source'}</span>
              </span>

              <span className="text-[10px] bg-lime-400/20 text-lime-300 font-bold px-2 py-0.5 rounded-full border border-lime-400/30 uppercase">
                {activeClip.sport}
              </span>
            </div>
          </div>

          {/* Provider embed or direct hosted stream. */}
          <div className="relative rounded-3xl overflow-hidden bg-black aspect-video border border-white/10 flex items-center justify-center group shadow-2xl">
            {getHostedVideoEmbedUrl(activeClip.videoUrl) ? (
              <iframe
                key={`${activeClip.id}_${activeClip.videoUrl}`}
                src={getHostedVideoEmbedUrl(activeClip.videoUrl)!}
                title={activeClip.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            ) : (
              <video
                key={`${activeClip.id}_${cachedUrls[activeClip.id] || activeClip.videoUrl}`}
                src={cachedUrls[activeClip.id] || activeClip.videoUrl || getSampleFallbackVideo(activeClip.sport)}
                controls
                autoPlay
                loop={isAutoLoopEnabled}
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            )}

            <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono font-bold text-white border border-white/20">
              00:{activeClip.durationSeconds < 10 ? `0${activeClip.durationSeconds}` : activeClip.durationSeconds} / 00:30
            </div>

            {/* Floating Overlay Quick Share Button on Video */}
            <button
              type="button"
              onClick={() => handleOpenQuickShare(activeClip)}
              className="absolute bottom-3 right-3 bg-gradient-to-r from-lime-400 to-emerald-400 text-black hover:scale-105 font-black italic text-xs px-3.5 py-2 rounded-2xl shadow-xl shadow-lime-400/30 border border-black/30 flex items-center space-x-1.5 transition z-10"
              title="Generate social media preview image for sharing"
            >
              <Sparkles className="w-4 h-4 fill-black" />
              <span>Quick Share Card</span>
            </button>

            {reportedClipIds.includes(activeClip.id) && (
              <div className="absolute top-3 right-3 bg-rose-950/90 text-rose-300 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black uppercase border border-rose-500/50 flex items-center space-x-1">
                <AlertTriangle className="w-3 h-3 text-rose-400" />
                <span>Flagged for Review</span>
              </div>
            )}
          </div>

          {/* Clip Details & Interactive Controls */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-black italic text-white flex items-center gap-2">
                  <span>{activeClip.title}</span>
                  {reportedClipIds.includes(activeClip.id) && (
                    <span className="text-[10px] bg-amber-400/20 text-amber-300 px-2 py-0.5 rounded-full font-bold border border-amber-400/30">
                      Reported ⚠️
                    </span>
                  )}
                </h3>
                <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">{activeClip.description}</p>
              </div>

              <div className="flex items-center space-x-2 shrink-0 flex-wrap gap-y-1">
                {/* Like Button */}
                <button
                  type="button"
                  onClick={() => handleLikeClip(activeClip.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition border ${
                    likedClipIds.includes(activeClip.id)
                      ? 'bg-rose-500 text-white border-rose-400 shadow-lg shadow-rose-500/30'
                      : 'bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${likedClipIds.includes(activeClip.id) ? 'fill-white text-white' : 'fill-rose-400 text-rose-400'}`} />
                  <span>{clipLikes[activeClip.id] ?? activeClip.likes}</span>
                </button>

                {/* Floating Quick Share Button */}
                <button
                  type="button"
                  onClick={() => handleOpenQuickShare(activeClip)}
                  className="px-3 py-1.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black border border-lime-400 rounded-xl text-xs font-black italic flex items-center space-x-1 transition shadow-md hover:scale-105"
                  title="Generate formatted social media preview card for Instagram/X/TikTok"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <span>Quick Share</span>
                </button>

                {/* Re-post to Feed Button */}
                <button
                  type="button"
                  onClick={() => setRepostingClip(activeClip)}
                  className="px-3 py-1.5 bg-lime-400/20 hover:bg-lime-400/30 text-lime-300 border border-lime-400/40 rounded-xl text-xs font-bold flex items-center space-x-1 transition shadow-sm"
                  title="Re-post clip with custom shoutout to your profile feed"
                >
                  <Repeat className="w-3.5 h-3.5 text-lime-400" />
                  <span>Re-post</span>
                </button>

                {/* Watch Party Button */}
                <button
                  type="button"
                  onClick={() => handleStartWatchParty(activeClip)}
                  className="px-3 py-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-400/40 rounded-xl text-xs font-bold flex items-center space-x-1 transition shadow-sm"
                  title="Create real-time Watch Party room for synchronized viewing and live chat"
                >
                  <Users className="w-3.5 h-3.5 text-sky-400" />
                  <span>Watch Party</span>
                </button>

                {/* Share Button */}
                <button
                  type="button"
                  onClick={() => {
                    const shareTitle = `Highlight Clip: ${activeClip.title}`;
                    const shareText = `Check out this ${activeClip.sport.toUpperCase()} highlight on Playground League! ${activeClip.description}`;
                    if (navigator.share) {
                      navigator.share({ title: shareTitle, text: shareText, url: window.location.href })
                        .then(() => {
                          setShareConfirmation({
                            type: 'highlight',
                            title: `Shared "${activeClip.title}"`,
                            subtitle: `Highlight reel clip broadcasted to social network! +25 XP credited.`,
                            sport: activeClip.sport,
                            xpAwarded: 25,
                          });
                          onEarnXp(25, 'Shared Highlight Clip');
                        })
                        .catch((err) => {
                          if (err.name !== 'AbortError') {
                            if (navigator.clipboard) {
                              navigator.clipboard.writeText(window.location.href);
                            }
                            setShareConfirmation({
                              type: 'highlight',
                              title: `Link Copied for "${activeClip.title}"`,
                              subtitle: `Highlight link copied to clipboard! Ready to post.`,
                              sport: activeClip.sport,
                              xpAwarded: 10,
                            });
                          }
                        });
                    } else {
                      if (navigator.clipboard) {
                        navigator.clipboard.writeText(window.location.href);
                      }
                      setShareConfirmation({
                        type: 'highlight',
                        title: `Link Copied for "${activeClip.title}"`,
                        subtitle: `Highlight link copied to clipboard! Ready to share on social feeds.`,
                        sport: activeClip.sport,
                        xpAwarded: 10,
                      });
                    }
                  }}
                  className="px-3 py-1.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 rounded-xl text-xs font-bold flex items-center space-x-1 border border-white/10 transition"
                >
                  <Share2 className="w-3.5 h-3.5 text-lime-400" />
                  <span>Share</span>
                </button>

                {/* Report Issue Button */}
                <button
                  type="button"
                  onClick={() => setReportingClip(activeClip)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center space-x-1 transition border ${
                    reportedClipIds.includes(activeClip.id)
                      ? 'bg-amber-950/80 text-amber-300 border-amber-500/40'
                      : 'bg-rose-950/60 hover:bg-rose-900/80 text-rose-200 border-rose-500/30'
                  }`}
                  title="Report video playback failure or technical issue"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  <span>{reportedClipIds.includes(activeClip.id) ? 'Reported' : 'Report Issue'}</span>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-indigo-300/70 font-semibold pt-2 border-t border-white/10">
              <span className="flex items-center text-lime-300 font-bold">
                <Eye className="w-3.5 h-3.5 mr-1 text-lime-400" />
                {(clipViews[activeClip.id] ?? activeClip.views)} Views
              </span>
              <span>Uploaded {activeClip.createdAt}</span>
            </div>
          </div>
        </div>

        {/* REEL GALLERY / FEED LIST */}
        <div className="lg:col-span-5 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-black italic uppercase text-sm text-white flex items-center">
              <Film className="w-4 h-4 text-lime-400 mr-2" />
              Community Reel Stream
            </h3>

            {/* Toggle Auto-Play on Hover */}
            <button
              type="button"
              onClick={() => setIsAutoPlayOnHoverEnabled(!isAutoPlayOnHoverEnabled)}
              className={`px-2.5 py-1 rounded-xl text-[10px] font-extrabold uppercase transition flex items-center space-x-1 border ${
                isAutoPlayOnHoverEnabled
                  ? 'bg-lime-400/20 text-lime-300 border-lime-400/40'
                  : 'bg-indigo-950 text-indigo-400 border-white/10'
              }`}
              title="Toggle video auto-preview on mouse hover"
            >
              <Zap className={`w-3 h-3 ${isAutoPlayOnHoverEnabled ? 'text-lime-400 fill-lime-400' : ''}`} />
              <span>Hover Preview: {isAutoPlayOnHoverEnabled ? 'ON' : 'OFF'}</span>
            </button>
          </div>

          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {sortedAndFilteredHighlights.length === 0 ? (
              <HighlightEmptyIllustration
                selectedSport={streamSportFilter}
                onResetSportFilter={() => setStreamSportFilter('all')}
                onUploadHighlight={() => onOpenUploadModal()}
              />
            ) : (
              sortedAndFilteredHighlights.map((clip) => {
                const isActive = activeClip.id === clip.id;
                const isHovered = isAutoPlayOnHoverEnabled && hoveredClipId === clip.id;

                return (
                  <div
                    key={clip.id}
                    onMouseEnter={() => setHoveredClipId(clip.id)}
                    onMouseLeave={() => setHoveredClipId(null)}
                    className={`group w-full p-3 rounded-2xl border text-left transition flex items-center space-x-3 relative ${
                      isActive
                        ? 'bg-lime-400 text-black font-black italic shadow-md border-lime-400'
                        : 'bg-indigo-950/80 text-white border-white/10 hover:bg-indigo-950'
                    }`}
                  >
                    <div
                      onClick={() => setActiveClip(clip)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setActiveClip(clip);
                        }
                      }}
                      className="flex items-center space-x-3 min-w-0 flex-1 text-left cursor-pointer"
                    >
                      {/* Thumbnail with Auto-play on Hover feature */}
                      <div className="relative w-24 h-16 rounded-xl overflow-hidden shrink-0 bg-black border border-white/10">
                        {isHovered ? (
                          getHostedVideoEmbedUrl(clip.videoUrl) ? (
                            <iframe
                              src={getHostedVideoEmbedUrl(clip.videoUrl)!}
                              title={clip.title}
                              className="w-full h-full"
                            />
                          ) : (
                            <video
                              src={cachedUrls[clip.id] || clip.videoUrl || getSampleFallbackVideo(clip.sport)}
                              autoPlay
                              loop
                              muted
                              playsInline
                              className="w-full h-full object-cover scale-105 transition duration-300"
                            />
                          )
                        ) : (
                          <img
                            src={clip.thumbnailUrl}
                            alt={clip.title}
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        )}

                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                          {isHovered ? (
                            <span className="px-1.5 py-0.5 bg-lime-400 text-black font-black text-[8px] uppercase rounded shadow animate-pulse">
                              Previewing ⚡
                            </span>
                          ) : (
                            <Play className={`w-5 h-5 ${isActive ? 'text-black fill-black' : 'text-white fill-white'}`} />
                          )}
                        </div>

                        <span className="absolute bottom-1 right-1 px-1 py-0.5 bg-black/80 text-[8px] font-mono text-white rounded">
                          {clip.durationSeconds}s
                        </span>
                      </div>

                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-xs font-bold line-clamp-1">{clip.title}</p>
                        <div className="flex items-center justify-between text-[10px] gap-2 pt-0.5">
                          <span className={`flex items-center space-x-1 ${isActive ? 'text-black/80 font-extrabold' : 'text-indigo-200/70'}`}>
                            <Eye className="w-3 h-3 shrink-0" />
                            <span>{(clipViews[clip.id] ?? clip.views)} views</span>
                          </span>

                          {/* Heart 'Like' Button on each clip card */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLikeClip(clip.id);
                            }}
                            className={`px-2 py-0.5 rounded-lg text-[10px] font-bold flex items-center space-x-1 transition border ${
                              likedClipIds.includes(clip.id)
                                ? isActive
                                  ? 'bg-rose-600 text-white border-rose-700'
                                  : 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                : isActive
                                  ? 'bg-black/10 hover:bg-black/20 text-black border-black/20'
                                  : 'bg-indigo-900/60 hover:bg-rose-950/60 text-indigo-200 hover:text-rose-300 border-white/10'
                            }`}
                            title="Like highlight clip"
                          >
                            <Heart className={`w-3 h-3 ${likedClipIds.includes(clip.id) ? 'fill-rose-500 text-rose-500' : 'text-rose-400'}`} />
                            <span>{clipLikes[clip.id] ?? clip.likes}</span>
                          </button>
                        </div>
                        {reportedClipIds.includes(clip.id) && (
                          <span className="text-[9px] text-amber-500 font-extrabold block">⚠️ Flagged for Admin</span>
                        )}
                      </div>
                    </div>

                    {/* Quick Report Button on Item */}
                    <button
                      type="button"
                      onClick={() => setReportingClip(clip)}
                      className={`p-2 rounded-xl transition ${
                        isActive
                          ? 'hover:bg-black/10 text-black'
                          : 'hover:bg-white/10 text-indigo-300 hover:text-amber-400'
                      }`}
                      title="Report video issue"
                    >
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* REPORT ISSUE TECHNICAL REVIEW MODAL */}
      {reportingClip && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="relative w-full max-w-lg bg-indigo-950 border border-rose-500/40 rounded-[2.5rem] p-6 space-y-5 shadow-2xl">
            <button
              onClick={() => setReportingClip(null)}
              className="absolute top-5 right-5 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="p-3 bg-rose-950 rounded-2xl border border-rose-500/40 shrink-0">
                <AlertTriangle className="w-6 h-6 text-rose-400" />
              </div>
              <div>
                <h3 className="text-lg font-black italic uppercase text-white">Report Video Technical Issue</h3>
                <p className="text-xs text-indigo-200/70 font-semibold">
                  Flag video for technical review by the Admin Dashboard team.
                </p>
              </div>
            </div>

            <div className="p-3.5 bg-indigo-900/60 rounded-2xl border border-white/10 text-xs text-white space-y-1">
              <p className="font-extrabold text-lime-300">{reportingClip.title}</p>
              <p className="text-indigo-200/70 text-[11px]">Sport: {reportingClip.sport} • ID: {reportingClip.id}</p>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-black uppercase tracking-wider text-indigo-300 block">
                Select Technical Issue:
              </label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full px-4 py-3 bg-indigo-900 border border-white/20 rounded-xl text-xs text-white font-bold focus:outline-none focus:border-rose-400"
              >
                <option value="Video playback failed / black screen">Video playback failed / black screen</option>
                <option value="Audio missing or out of sync">Audio missing or out of sync</option>
                <option value="Buffering freeze or broken stream">Buffering freeze or broken stream</option>
                <option value="Incorrect video content or sport tag">Incorrect video content or sport tag</option>
              </select>

              <label className="text-xs font-black uppercase tracking-wider text-indigo-300 block pt-1">
                Additional Technical Notes (Optional):
              </label>
              <textarea
                value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                placeholder="Describe what happened (e.g. video freezes at 0:05)..."
                rows={3}
                className="w-full p-3 bg-indigo-900 border border-white/20 rounded-xl text-xs text-white font-medium focus:outline-none focus:border-rose-400"
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setReportingClip(null)}
                className="px-4 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-white text-xs font-extrabold uppercase rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitIssueReport}
                className="px-5 py-2.5 bg-rose-500 hover:bg-rose-400 text-white text-xs font-black italic uppercase rounded-xl shadow-lg transition flex items-center space-x-1.5"
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Submit to Admin Review</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RE-POST TO PROFILE FEED MODAL */}
      {repostingClip && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-indigo-950 border border-lime-400/50 rounded-[2.5rem] p-6 max-w-lg w-full space-y-5 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setRepostingClip(null)}
              className="absolute top-5 right-5 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="p-3 bg-lime-400/20 rounded-2xl border border-lime-400/40 shrink-0">
                <Repeat className="w-6 h-6 text-lime-400" />
              </div>
              <div>
                <h3 className="text-lg font-black italic uppercase text-white">Re-post to Profile Feed</h3>
                <p className="text-xs text-indigo-200/70 font-semibold">
                  Share this highlight with your followers & earn +25 XP!
                </p>
              </div>
            </div>

            {/* Original Clip Card Preview */}
            <div className="flex items-center space-x-3 p-3 bg-indigo-900/80 rounded-2xl border border-white/10">
              <img
                src={repostingClip.thumbnailUrl || getSampleFallbackVideo(repostingClip.sport)}
                alt={repostingClip.title}
                className="w-16 h-12 object-cover rounded-xl border border-white/10"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-black text-white line-clamp-1">{repostingClip.title}</p>
                <p className="text-[10px] text-lime-300/80 font-mono font-bold uppercase">{repostingClip.sport} • {repostingClip.views} views</p>
              </div>
            </div>

            {/* Shoutout Message Input */}
            <div className="space-y-2">
              <label className="text-xs font-black uppercase tracking-wider text-indigo-300 block">
                Add Your Custom Shoutout Message:
              </label>
              <textarea
                value={repostShoutoutMessage}
                onChange={(e) => setRepostShoutoutMessage(e.target.value)}
                placeholder="Insane dunk by Marcus! You gotta check this out 🔥"
                rows={3}
                className="w-full p-3 bg-indigo-900 border border-white/20 rounded-xl text-xs text-white font-medium focus:outline-none focus:border-lime-400"
              />

              {/* Quick Preset Tags */}
              <div className="flex items-center gap-1.5 flex-wrap pt-1">
                {['🔥 Unreal Skill!', '🚀 MVP Move!', '👏 Massive Respect!', '💯 Game Changer!'].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setRepostShoutoutMessage((prev) => (prev ? `${prev} ${preset}` : preset))}
                    className="px-2.5 py-1 bg-indigo-900 hover:bg-lime-950/60 text-indigo-200 hover:text-lime-300 text-[10px] font-bold rounded-lg border border-white/10 transition"
                  >
                    + {preset}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setRepostingClip(null)}
                className="px-4 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-white text-xs font-extrabold uppercase rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitRepost}
                className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black text-xs font-black italic uppercase rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
              >
                <Repeat className="w-4 h-4" />
                <span>Re-post to Feed (+25 XP)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EPHEMERAL WATCH PARTY ROOM MODAL */}
      {isWatchPartyOpen && watchPartyClip && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-lg flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-indigo-950 border border-sky-400/50 rounded-[2.5rem] max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
            {/* Watch Party Header */}
            <div className="p-4 sm:p-5 bg-gradient-to-r from-sky-950 via-indigo-950 to-purple-950 border-b border-white/10 flex items-center justify-between shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-sky-500/20 rounded-2xl border border-sky-400/40 text-sky-400 shrink-0">
                  <Users className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] bg-red-500/20 text-red-300 border border-red-500/40 px-2 py-0.5 rounded-full font-black uppercase tracking-wider flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
                      LIVE WATCH PARTY
                    </span>
                    <span className="text-[10px] text-sky-300 font-mono font-bold">{watchPartyRoomId}</span>
                  </div>
                  <h3 className="text-base font-black italic text-white line-clamp-1">{watchPartyClip.title}</h3>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(window.location.href);
                    setWatchPartyCopied(true);
                    setTimeout(() => setWatchPartyCopied(false), 2000);
                  }}
                  className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-sky-300 text-xs font-bold rounded-xl border border-sky-400/30 transition flex items-center space-x-1"
                >
                  {watchPartyCopied ? <Check className="w-3.5 h-3.5 text-lime-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{watchPartyCopied ? 'Link Copied!' : 'Invite Friends'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsWatchPartyOpen(false)}
                  className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Watch Party Main Grid Body */}
            <div className="grid grid-cols-1 lg:grid-cols-12 flex-1 overflow-hidden min-h-0">
              {/* Left Column: Synchronized Player Stage */}
              <div className="lg:col-span-8 p-4 bg-black/60 flex flex-col justify-between relative overflow-hidden space-y-3">
                {/* Floating Reactions Overlay Layer */}
                <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
                  {watchPartyFloatingReactions.map((rx) => (
                    <div
                      key={rx.id}
                      style={{ left: `${rx.left}%` }}
                      className="absolute bottom-16 text-3xl animate-bounce duration-1000 transition-all opacity-90 scale-125"
                    >
                      {rx.emoji}
                    </div>
                  ))}
                </div>

                {/* Video Player */}
                <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black border border-white/10 group flex items-center justify-center">
                  {getHostedVideoEmbedUrl(watchPartyClip.videoUrl) ? (
                    <iframe
                      src={getHostedVideoEmbedUrl(watchPartyClip.videoUrl)!}
                      title={watchPartyClip.title}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  ) : (
                    <video
                      ref={watchPartyVideoRef}
                      src={cachedUrls[watchPartyClip.id] || watchPartyClip.videoUrl || getSampleFallbackVideo(watchPartyClip.sport)}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  )}

                  {/* Synced Host Tag Overlay */}
                  <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-sky-300 border border-sky-400/30 flex items-center space-x-1.5 z-10">
                    <Radio className="w-3 h-3 text-sky-400 animate-pulse" />
                    <span>Real-Time Playback Synced</span>
                  </div>
                </div>

                {/* Synchronized Control Bar */}
                <div className="p-3 bg-indigo-950/90 rounded-2xl border border-white/10 flex flex-col space-y-2 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={handleToggleWatchPartyPlayback}
                        className="p-2 bg-sky-500 hover:bg-sky-400 text-black rounded-xl font-black transition"
                      >
                        {watchPartyIsPlaying ? <Pause className="w-4 h-4 fill-black" /> : <Play className="w-4 h-4 fill-black" />}
                      </button>
                      <span className="text-xs font-mono font-bold text-sky-300">
                        {watchPartyIsPlaying ? 'LIVE SYNC PLAYING' : 'PAUSED BY HOST'}
                      </span>
                    </div>

                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] font-bold uppercase text-indigo-300/80 mr-1">Float Reaction:</span>
                      {['🔥', '😱', '👏', '🎯', '💥', '💯'].map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => handleTriggerWatchPartyReaction(emoji)}
                          className="px-2 py-1 bg-indigo-900 hover:bg-sky-900/60 rounded-lg text-sm transition hover:scale-125 transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Watch Party Live Chat & Members Panel */}
              <div className="lg:col-span-4 bg-indigo-950/95 border-l border-white/10 flex flex-col h-full overflow-hidden">
                {/* Panel Tabs */}
                <div className="flex border-b border-white/10 text-xs font-bold shrink-0">
                  <button
                    type="button"
                    onClick={() => setWatchPartyActiveTab('chat')}
                    className={`flex-1 py-3 text-center transition flex items-center justify-center space-x-1.5 border-b-2 ${
                      watchPartyActiveTab === 'chat'
                        ? 'border-sky-400 text-sky-300 bg-sky-500/10'
                        : 'border-transparent text-indigo-300/70 hover:text-white'
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Watch Chat</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setWatchPartyActiveTab('members')}
                    className={`flex-1 py-3 text-center transition flex items-center justify-center space-x-1.5 border-b-2 ${
                      watchPartyActiveTab === 'members'
                        ? 'border-sky-400 text-sky-300 bg-sky-500/10'
                        : 'border-transparent text-indigo-300/70 hover:text-white'
                    }`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    <span>In Room (4)</span>
                  </button>
                </div>

                {watchPartyActiveTab === 'chat' ? (
                  <div className="flex-1 flex flex-col justify-between overflow-hidden p-3 space-y-3">
                    {/* Chat Messages Stream */}
                    <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 max-h-[320px] lg:max-h-full">
                      {watchPartyMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`text-xs p-2.5 rounded-xl border ${
                            msg.isSystem
                              ? 'bg-sky-950/40 border-sky-500/30 text-sky-200 italic'
                              : 'bg-indigo-900/60 border-white/10 text-white'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-1.5">
                              <img src={msg.senderAvatar} alt={msg.senderName} className="w-4 h-4 rounded-full object-cover" />
                              <span className="font-bold text-[11px] text-sky-300">{msg.senderName}</span>
                            </div>
                            <span className="text-[9px] text-indigo-300/60 font-mono">{msg.timestamp}</span>
                          </div>
                          <p className="text-[11px] leading-relaxed">{msg.text}</p>
                        </div>
                      ))}
                    </div>

                    {/* Chat Input Box */}
                    <form onSubmit={handleSendWatchPartyMessage} className="flex items-center space-x-2 pt-1 border-t border-white/10 shrink-0">
                      <input
                        type="text"
                        value={watchPartyChatInput}
                        onChange={(e) => setWatchPartyChatInput(e.target.value)}
                        placeholder="Discuss play in real-time..."
                        className="flex-1 px-3 py-2 bg-indigo-900 border border-white/20 rounded-xl text-xs text-white placeholder-indigo-300/50 focus:outline-none focus:border-sky-400"
                      />
                      <button
                        type="submit"
                        className="p-2 bg-sky-500 hover:bg-sky-400 text-black rounded-xl font-bold transition shrink-0"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                    <p className="text-xs font-bold text-sky-300 uppercase tracking-wider">Active Watchers in Room:</p>
                    <div className="space-y-2">
                      {[
                        { name: `${user?.name || 'Athlete'} (Host)`, role: 'Host', avatar: user?.avatar || '' },
                        { name: 'Marcus Vance', role: 'Viewer', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200' },
                        { name: 'Elena Rostova', role: 'Viewer', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200' },
                        { name: 'Devon Allen', role: 'Viewer', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200' },
                      ].map((member, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2.5 bg-indigo-900/50 rounded-xl border border-white/10">
                          <div className="flex items-center space-x-2.5">
                            <div className="relative">
                              <img src={member.avatar} alt={member.name} className="w-7 h-7 rounded-full object-cover" />
                              <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-400 ring-1 ring-black" />
                            </div>
                            <span className="text-xs font-bold text-white">{member.name}</span>
                          </div>
                          <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                            member.role === 'Host' ? 'bg-sky-500 text-black' : 'bg-indigo-900 text-indigo-300 border border-white/10'
                          }`}>
                            {member.role}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUICK SHARE SOCIAL MEDIA PREVIEW IMAGE GENERATOR MODAL */}
      {isQuickShareModalOpen && quickShareClip && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 rounded-[2.5rem] border-2 border-lime-400 p-6 md:p-8 max-w-3xl w-full space-y-6 shadow-2xl relative">
            <button
              onClick={() => setIsQuickShareModalOpen(false)}
              className="absolute top-5 right-5 p-2 bg-indigo-900 hover:bg-indigo-800 text-white rounded-full transition border border-white/10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-tr from-lime-400 to-emerald-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
                <Camera className="w-6 h-6 stroke-[2.5]" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-xl font-black italic uppercase text-white tracking-wide">
                    Formatted Social Media Preview Card
                  </h2>
                  <span className="px-2.5 py-0.5 bg-lime-400 text-black text-[10px] font-mono font-black rounded-full uppercase">
                    1200x630 Card
                  </span>
                </div>
                <p className="text-xs text-indigo-200/80 mt-0.5">
                  Generate an official high-resolution social graphic for Instagram, X, TikTok & Snapchat stories.
                </p>
              </div>
            </div>

            {/* Canvas Element (Hidden or Live Preview) */}
            <canvas ref={quickShareCanvasRef} className="hidden" />

            {/* Generated Preview Image Box */}
            <div className="relative rounded-2xl overflow-hidden border-2 border-lime-400/40 shadow-2xl bg-black aspect-[1200/630] flex items-center justify-center group">
              {quickShareImageUrl ? (
                <img
                  src={quickShareImageUrl}
                  alt="Social Media Preview Card"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="p-8 text-center space-y-3">
                  <Sparkles className="w-8 h-8 text-lime-400 animate-spin mx-auto" />
                  <p className="text-xs font-mono font-bold text-indigo-200">Generating formatted social graphic...</p>
                </div>
              )}

              <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono text-lime-300 border border-lime-400/40">
                HD Social Graphic Ready
              </div>
            </div>

            {/* Modal Action Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-white/10">
              <div className="text-xs text-indigo-200/80 font-mono">
                <span>Includes stats, verified badge & Playground League watermark.</span>
              </div>

              <div className="flex items-center space-x-2 w-full sm:w-auto">
                {/* Download PNG Button */}
                {quickShareImageUrl && (
                  <a
                    href={quickShareImageUrl}
                    download={`highlight_reel_${quickShareClip.sport}_${quickShareClip.id}.png`}
                    onClick={() => {
                      triggerHaptic('success');
                      onEarnXp(20, 'Downloaded Social Media Highlight Card');
                      setReportToastMessage('🖼️ Social preview image downloaded! (+20 XP)');
                      setTimeout(() => setReportToastMessage(null), 3500);
                    }}
                    className="flex-1 sm:flex-none px-4 py-2.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 hover:scale-105 transition flex items-center justify-center space-x-2"
                  >
                    <Download className="w-4 h-4 stroke-[2.5]" />
                    <span>Download PNG</span>
                  </a>
                )}

                {/* Copy Link Button */}
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    navigator.clipboard?.writeText(window.location.href);
                    setQuickShareCopied(true);
                    setShareConfirmation({
                      type: 'highlight',
                      title: `Share Card Ready for "${quickShareClip.title}"`,
                      subtitle: `Copied link to clipboard & social preview generated. +20 XP awarded!`,
                      sport: quickShareClip.sport,
                      xpAwarded: 20,
                    });
                    onEarnXp(20, 'Shared Highlight Social Card');
                    setTimeout(() => setQuickShareCopied(false), 2500);
                  }}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-white font-bold text-xs rounded-xl border border-white/15 transition flex items-center justify-center space-x-1.5"
                >
                  <Copy className="w-4 h-4" />
                  <span>{quickShareCopied ? 'Link Copied! ✓' : 'Copy Share Link'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Confirmation Feedback Notification Overlay */}
      <ShareConfirmationNotification
        data={shareConfirmation}
        onClose={() => setShareConfirmation(null)}
      />
    </div>
  );
};
